import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Mail, Loader2, Save, CheckSquare, Square, ChevronDown, ChevronRight } from "lucide-react";
import { TAXONOMY } from "@/data/taxonomy";
import { toast } from "sonner";

/**
 * CustomerPromoCategoriesCard
 *
 * Two-level checkbox tree (top-level group + nested subcategories) that lets a
 * customer decide which businesses can message them offers. Every box defaults
 * to CHECKED so the surface area starts at "receive everything"; un-checking
 * a leaf only blocks promos from businesses tagged with that specific
 * category, while un-checking the group header blocks the whole bucket.
 *
 * The master "Receive promotional messages" switch globally pauses every send,
 * irrespective of the per-category state.
 */
const GROUPS = TAXONOMY.map((t) => t.group);
const ALL_LEAVES = Array.from(new Set(TAXONOMY.flatMap((t) => t.items)));

export default function CustomerPromoCategoriesCard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wantsAll, setWantsAll] = useState(true);
  const [groupsSel, setGroupsSel] = useState(new Set());
  const [leavesSel, setLeavesSel] = useState(new Set());
  const [openGroups, setOpenGroups] = useState(new Set()); // accordion state
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return; // lazy-load on first expand
    api
      .get("/me/customer/prefs")
      .then(({ data }) => {
        setWantsAll(data.wants_promo_emails !== false);
        const storedGroups = data.promo_category_groups || [];
        const storedLeaves = data.promo_categories || [];
        const isFresh = storedGroups.length === 0 && storedLeaves.length === 0;
        setGroupsSel(isFresh ? new Set(GROUPS) : new Set(storedGroups));
        setLeavesSel(isFresh ? new Set(ALL_LEAVES) : new Set(storedLeaves));
      })
      .catch((e) => console.warn("prefs load failed:", e))
      .finally(() => setLoading(false));
  }, [open]);

  const toggleGroup = (group, items) => {
    setDirty(true);
    setGroupsSel((prevG) => {
      const nextG = new Set(prevG);
      const turningOff = nextG.has(group);
      if (turningOff) nextG.delete(group);
      else nextG.add(group);
      // Cascade to leaves: when a group flips, every leaf inside follows.
      setLeavesSel((prevL) => {
        const nextL = new Set(prevL);
        items.forEach((leaf) => {
          if (turningOff) nextL.delete(leaf);
          else nextL.add(leaf);
        });
        return nextL;
      });
      return nextG;
    });
  };

  const toggleLeaf = (group, leaf, items) => {
    setDirty(true);
    setLeavesSel((prev) => {
      const next = new Set(prev);
      if (next.has(leaf)) next.delete(leaf);
      else next.add(leaf);
      // Sync the parent: if at least one leaf in the group is checked, the
      // group counts as "on". If none are checked, group flips off.
      const anyChecked = items.some((it) => next.has(it));
      setGroupsSel((prevG) => {
        const nextG = new Set(prevG);
        if (anyChecked) nextG.add(group);
        else nextG.delete(group);
        return nextG;
      });
      return next;
    });
  };

  const toggleOpen = (group) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const selectAll = () => {
    setDirty(true);
    setGroupsSel(new Set(GROUPS));
    setLeavesSel(new Set(ALL_LEAVES));
  };

  const clearAll = () => {
    setDirty(true);
    setGroupsSel(new Set());
    setLeavesSel(new Set());
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/customer/prefs", {
        wants_promo_emails: wantsAll,
        promo_category_groups: Array.from(groupsSel),
        promo_categories: Array.from(leavesSel),
      });
      toast.success("Promo preferences saved");
      setDirty(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const totalLeaves = ALL_LEAVES.length;
  const summary = useMemo(() => {
    if (leavesSel.size === totalLeaves && groupsSel.size === GROUPS.length) {
      return `All ${totalLeaves} subcategories selected — you'll receive promotions from every opted-in business.`;
    }
    if (leavesSel.size === 0 && groupsSel.size === 0) {
      return "No categories selected — you'll still receive promotions from every opted-in business. Switch the master toggle off above to fully opt out.";
    }
    return `Receiving from ${groupsSel.size}/${GROUPS.length} groups · ${leavesSel.size}/${totalLeaves} subcategories. Unchecked items are skipped.`;
  }, [leavesSel.size, groupsSel.size, totalLeaves]);

  // Collapsed state — just the launcher button.
  if (!open) {
    return (
      <Card className="rounded-md border border-border p-6" data-testid="customer-promo-prefs-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-full bg-[#B31E1E]/5 text-[#B31E1E] flex items-center justify-center shrink-0">
              <Mail size={18} />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-xl">Promotional message preferences</h3>
              <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
                Choose which categories of businesses can message you offers.
                Open the menu to fine-tune by category and subcategory, or
                pause all promotional messages entirely.
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
            data-testid="customer-promo-open-button"
          >
            <Mail size={14} className="mr-1.5" /> Manage promotional preferences
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="rounded-md border border-border p-6" data-testid="customer-promo-prefs-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Mail size={16} className="text-[#B31E1E]" />
            <h3 className="font-display text-xl">Promotional message preferences</h3>
            {!loading && (
              <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-[#B31E1E]/40 text-[#B31E1E]" data-testid="customer-promo-summary-badge">
                {leavesSel.size}/{totalLeaves} subcategories
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Tick the businesses you want to hear from. Expanding any group
            reveals every subcategory beneath it so you can fine-tune (e.g.
            keep <em>Plumbing</em> on but switch off <em>Pool Service</em>).
            Toggle the master switch to pause every promotional message.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          className="rounded-[35px] shadow-sm"
          data-testid="customer-promo-close-button"
        >
          Close
        </Button>
      </div>

      <div className="mt-5 flex items-center justify-between border border-border rounded-md px-3 py-2.5 bg-gray-50">
        <div>
          <Label className="text-sm font-medium">Receive promotional messages</Label>
          <p className="text-xs text-gray-500 mt-0.5">
            Master switch — when off, no business can send promotions,
            regardless of the categories you pick below.
          </p>
        </div>
        <Switch
          checked={wantsAll}
          onCheckedChange={(v) => { setWantsAll(v); setDirty(true); }}
          data-testid="customer-promo-master-switch"
        />
      </div>

      <div className={`mt-5 ${wantsAll ? "" : "opacity-50 pointer-events-none"}`}>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-mono uppercase tracking-wider text-gray-500">
            Categories &amp; subcategories
          </Label>
          <div className="flex items-center gap-3 text-[11px] font-mono">
            <button
              type="button"
              onClick={selectAll}
              className="inline-flex items-center gap-1 text-[#B31E1E] hover:underline"
              data-testid="customer-promo-select-all"
            >
              <CheckSquare size={11} /> All
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 hover:underline"
              data-testid="customer-promo-clear-all"
            >
              <Square size={11} /> None
            </button>
            <button
              type="button"
              onClick={() => setOpenGroups(new Set(GROUPS))}
              className="text-gray-500 hover:text-gray-700 hover:underline"
              data-testid="customer-promo-expand-all"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={() => setOpenGroups(new Set())}
              className="text-gray-500 hover:text-gray-700 hover:underline"
              data-testid="customer-promo-collapse-all"
            >
              Collapse all
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="border border-border rounded-md divide-y divide-border bg-white" data-testid="customer-promo-tree">
            {TAXONOMY.map((t) => {
              const slug = t.group.replace(/\s+/g, "-").toLowerCase();
              const groupChecked = groupsSel.has(t.group);
              const checkedLeafCount = t.items.reduce((n, leaf) => n + (leavesSel.has(leaf) ? 1 : 0), 0);
              const partial = checkedLeafCount > 0 && checkedLeafCount < t.items.length;
              const isOpen = openGroups.has(t.group);
              return (
                <div key={t.group} data-testid={`customer-promo-group-${slug}`}>
                  <div className={`flex items-center gap-2 px-3 py-2 ${groupChecked ? "bg-[#B31E1E]/5" : "hover:bg-gray-50"}`}>
                    <Checkbox
                      checked={partial ? "indeterminate" : groupChecked}
                      onCheckedChange={() => toggleGroup(t.group, t.items)}
                      data-testid={`customer-promo-checkbox-${slug}`}
                    />
                    <button
                      type="button"
                      onClick={() => toggleOpen(t.group)}
                      className="flex-1 flex items-center gap-2 text-left"
                      data-testid={`customer-promo-toggle-${slug}`}
                    >
                      {isOpen ? <ChevronDown size={13} className="text-gray-500" /> : <ChevronRight size={13} className="text-gray-500" />}
                      <span className="text-sm font-medium text-gray-900">{t.group}</span>
                      <span className="text-[11px] font-mono text-gray-500">
                        {checkedLeafCount}/{t.items.length}
                      </span>
                    </button>
                  </div>
                  {isOpen && (
                    <div
                      className="px-3 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 bg-gray-50/40"
                      data-testid={`customer-promo-leaves-${slug}`}
                    >
                      {t.items.map((leaf) => {
                        const leafSlug = leaf.replace(/\s+/g, "-").toLowerCase();
                        const checked = leavesSel.has(leaf);
                        return (
                          <label
                            key={leaf}
                            className={`flex items-center gap-2 rounded-sm px-2 py-1 text-sm cursor-pointer ${
                              checked ? "text-gray-900" : "text-gray-500"
                            } hover:bg-white`}
                            data-testid={`customer-promo-leaf-${leafSlug}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleLeaf(t.group, leaf, t.items)}
                              data-testid={`customer-promo-leaf-checkbox-${leafSlug}`}
                            />
                            <span className="truncate" title={leaf}>{leaf}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && (
          <p
            className={`mt-2 text-[11px] font-mono ${
              leavesSel.size === 0 ? "text-amber-700" : "text-gray-500"
            }`}
            data-testid="customer-promo-summary"
          >
            {summary}
          </p>
        )}
      </div>

      <div className="mt-5 flex items-center gap-2">
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving || loading}
          className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
          data-testid="customer-promo-save-button"
        >
          {saving ? (
            <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</>
          ) : (
            <><Save size={14} className="mr-1.5" /> Save preferences</>
          )}
        </Button>
        {!dirty && !loading && (
          <span className="text-xs font-mono text-gray-400">All changes saved</span>
        )}
      </div>
    </Card>
  );
}
